import { apply, put, delay, select, all } from 'redux-saga/effects';
import { errorNotification, successNotification } from '../reducers/notification.reducers';
import {
  loadOrdersSucceeded,
  loadOrdersFailed,
  loadOrderSucceeded,
  loadOrderFailed,
  loadReportsSucceeded,
  loadReportsFailed,
  removeObsoleteOrders,
  selectOrder,
  proceedOrder as proceedOrderAction,
  declineOrder as declineOrderAction,
  loadOrder as loadOrderAction,
  setOrderSearchValue,
  addOrderToHistory,
  startPrintOrder,
  mapOrder,
  addNewOrderToQueue,
  setNewOrderAlertVisible,
  resetOrderSearchValue,
  actionOnOrdercard as actionOnOrdercardAction,
  proceedAllVisibleOrdersDone,
  finishCheckExportPossible,
} from '../reducers/order.reducers';

import {
  setAlertSoundState,
} from '../reducers/app.reducers';

import isRKSVCode from '../utils/is-rksv-code';
import { flatten, groupBy, sum } from 'ramda';

import { getVisibleOrders } from '../reselect/order.selector';
import moment from 'moment';

const cleanLeadingZeros = (text) => {
  if (!text) {
    return text;
  }

  return text.replace(/\b0+/g, '');
};

// eslint-disable-next-line import/prefer-default-export
export function* loadOrders(orderApi) {
  try {
    yield delay(1000);

    const response = yield apply(
      orderApi,
      orderApi.loadOrders,
    );

    if (response.errorCode) {
      yield put(loadOrdersFailed({ message: response.message || 'Failed to login' }));
      yield put(errorNotification(response.message || 'Failed to login'));
    } else {
      yield put(loadOrdersSucceeded({ orders: response }));
    }
  } catch (e) {
    yield put(loadOrdersFailed({ message: 'Failed to login' }));
  }
}


export function* loadReports(orderApi) {
  try {
    const response = yield apply(
      orderApi,
      orderApi.loadReports,
    );

    if (response.errorCode) {
      yield put(loadReportsFailed({ message: response.message || 'Failed load reports' }));
      yield put(errorNotification(response.message || 'Failed load reports'));
    } else {
      yield put(loadReportsSucceeded({ reports: response }));
    }
  } catch (e) {
    throw e;
    yield put(loadReportsFailed({ message: 'Failed to reports' }));
  }
}


export function* startCheckExportPossible(orderApi, { payload }) {
  try {
    const response = yield apply(
      orderApi,
      orderApi.ninjasCheckPossible,
      [payload.id],
    );

    if (!response || !response.possible) {
      yield put(errorNotification('Zustellung nicht möglich'));
      yield put(finishCheckExportPossible(payload));
      return;
    } else {
      const responseExport = yield apply(
        orderApi,
        orderApi.ninjasExportOrder,
        [payload.id],
      );

      if (responseExport) {
        if (responseExport.errorCode) {
          yield put(errorNotification('Bestellung konnte nicht zur Zustellung übergeben werden.'));
        } else {
          yield put(successNotification('Bestellung erfolgreich zur Zustellung übergeben.'));
        }
      }
    }
  } catch (e) {
    console.error(e);
    yield put(errorNotification('Zustellung konnte nicht übergeben werden'));
  }

  yield put(finishCheckExportPossible(payload))
}

export function* loadOrder(orderApi, { payload }) {
  const { orderId, isNew } = payload;
  try {
    const response = yield apply(
      orderApi,
      orderApi.loadOrder,
      [orderId],
    );

    if (response.errorCode) {
      yield put(loadOrderFailed(orderId));
      yield put(errorNotification(response.message || 'Failed to load order'));
    } else {
      if (response.spotId && response.isSpotAssignedToWaiter == false) {
        yield delay(350);
        yield put(removeObsoleteOrders());
        return;
      }

      if (response.show && isNew) {
        yield put(setAlertSoundState(true));
      }

      const order = mapOrder(response);
      const localSettings = yield select(state => state.app.localSettings);

      if (response.show && localSettings.useEposPrinter && localSettings.autoprintNewOrders && !(yield select(state => state.orders.orders)).flatMap(order => order.id).includes(order.id)) {
        yield put(startPrintOrder(order));

        if (localSettings.printOrderTwice) {
          yield put(startPrintOrder(order));
        }
      }

      if (localSettings.openAlertForNewOrder && !(yield select(state => state.orders.orders)).flatMap(order => order.id).includes(order.id)) {
        if (isNew) {
          yield put(setNewOrderAlertVisible(true));
        }

        yield put(addNewOrderToQueue(order));
      }

      yield put(loadOrderSucceeded({ order: response }));
      yield delay(350);
      yield put(removeObsoleteOrders());
    }
  } catch (e) {
    yield put(loadOrdersFailed(orderId));
  }
}

export function* proceedOrder(orderApi, { payload }) {
  try {
    yield delay(500);

    const restrictPickupToToday = yield select(state => state.app.localSettings.restrictPickupToToday)

    if (restrictPickupToToday && payload.item) {      
      const pickupMoment = moment(payload.item.pickupTimeNumeric).add(2, 'hours'); // prevent timezone issues
      const endOfDay = moment().endOf('day');
      const isFutureDate = pickupMoment.isAfter(endOfDay);
      if (isFutureDate) {
        yield put(errorNotification(`Bestellung für ${pickupMoment.format('DD.MM.')} kann noch nicht bestätigt werden!`));
        return;
      }
    }
    
    const orderId = payload.item ? payload.item.id : payload;
    const printerId = yield select(state => state.app.localSettings.printerId)

    const response = yield apply(
      orderApi,
      orderApi.proceedOrder,
      [orderId, !!printerId ? printerId : null],
    );

    if (response && response.errorCode) {
      yield put(errorNotification(response.message || 'Failed to proceed order'));
    } else {
      const signalRconnected = yield select(state => state.app.signalRconnected);

      if (!signalRconnected) {
        yield put(loadOrderAction({ orderId }));
      }
    }
  } catch (e) {
    yield put(errorNotification('Failed to proceed order'));
  }
}

export function* proceedAllVisibleOrders(orderApi) {
  try {
    const visibleOrders = yield select(state => getVisibleOrders(state));

    for (let i = 0; i < visibleOrders.length; i++) {
      const order = visibleOrders[i];
      yield put(actionOnOrdercardAction({
        order,
        proceeded: true,
        description: order.proceedText
      }))
      yield delay(100);   
    }
  } catch(e) {
    //
  } finally {
    yield put(proceedAllVisibleOrdersDone()); 
  }
}

export function* declineOrder(orderApi, { payload }) {
  try {
    const response = yield apply(
      orderApi,
      orderApi.declineOrder,
      [payload],
    );

    if (response.errorCode) {
      yield put(errorNotification(response.message || 'Failed to decline order'));
    }
  } catch (e) {
    yield put(errorNotification('Failed to decline order'));
  }
}

export function* printAllLabels(orderApi, { payload }) {
  try {
    const user = yield select(state => state.authentication.user);

    const locationId = user.staffLocationID;

    const response = yield apply(
      orderApi,
      orderApi.printAllLabels,
      [locationId],
    );

    if (response.errorCode) {
      yield put(errorNotification(response.message || 'Failed to download labels'));
    }
  } catch (e) {
    console.log(e);
    yield put(errorNotification('Failed to download labels'));
  }
}


export function* downloadLabels(orderApi, { payload }) {
  try {
    const user = yield select(state => state.authentication.user);

    const locationId = user.staffLocationID;

    const response = yield apply(
      orderApi,
      orderApi.downloadLabels,
      [{ locationId }],
    );

    if (response.errorCode) {
      yield put(errorNotification(response.message || 'Failed to download labels'));
    }
  } catch (e) {
    console.log(e);
    yield put(errorNotification('Failed to download labels'));
  }
}

export function* scheduleSearchFieldReset() {
  try {
    const localSettings = yield select(state => state.app.localSettings);

    if (localSettings && localSettings.resetSearch) {
      const resetDelay = (localSettings.resetSearchValue || 60);
      const delayMs = Math.max(15, Number(resetDelay)) * 1000;
      console.log(`Reseting search in ${resetDelay} seconds`);

      yield delay(1000);
      yield delay(delayMs);
      yield put(setOrderSearchValue(''));
    }
  } catch (error) {
    console.error('Failed to reset search', error);
  }
}

function mergeOrders(filteredOrders) {
  const groupedOrders = groupBy((o => {
    return `${o.parentOrderGuid}_${o.pickupDate}`;
  }))(filteredOrders);
  let mergedOrders = [];

  Object.keys(groupedOrders).forEach((groupKey) => {
    if (!groupKey) {
      mergedOrders = mergedOrders.concat(groupedOrders[groupKey]);
      return;
    }

    if (groupedOrders[groupKey].length == 1) {
      mergedOrders = mergedOrders.concat(groupedOrders[groupKey]);
      return;
    }

    mergedOrders = mergedOrders.concat({
      ...groupedOrders[groupKey][0],
      articles: flatten(groupedOrders[groupKey].map(x => x.articles)),
      totalPrice: sum(groupedOrders[groupKey].map(x => x.totalPrice)),
      subOrders: groupedOrders[groupKey].map(x => x.id),
      parentGuids: groupedOrders[groupKey].map(x => x.parentOrderGuid),
    })
  });

  return mergedOrders;
}

export function* handleScan(orderApi, { payload }) {
  try {
    if (!payload) {
      return;
    }

    let cleanedPayload = payload.trim();

    let order = null;
    let didMatchUser = false;
    let didMatchRKSV = false;
    const localSettings = yield select(state => state.app.localSettings);
    const orders = yield select(state => state.orders.orders);
    const { selectedMode, ordersEndDate } = yield select(state => state.orders);
    
    if (isRKSVCode(cleanedPayload)) {
      const response = yield apply(orderApi, orderApi.findOrderByInvoice, [{ code: cleanedPayload, locationId: 0 }]);

      if (response) {
        if (response.errorCode || !response.guid) {
          yield put(errorNotification(`Bestellung nicht gefunden! (RKSV, ${cleanedPayload})`));
        } else {
          order = orders.find(o => o.guid == response.guid || o.parentOrderId == response.orderID);
          if (order) {
            didMatchRKSV = true;
          }
        }
      }
    } else {
      // try to get last guid in case we got code to long
      if (cleanedPayload.length > 36) {
        cleanedPayload = cleanedPayload.slice(cleanedPayload.length - 36);
      }

      cleanedPayload = cleanedPayload.replace(/[^0-9]+/g, '');

      const matchingOrders = orders.filter((o) => {
        const matchesGuid = o.guid && o.guid.replace(/[^0-9]+/g, '') === cleanedPayload;
        const matchesPickupCode = o.pickupCode && o.pickupCode === cleanedPayload;
        const matchesParentGuid = o.parentOrderGuid && o.parentOrderGuid.replace(/[^0-9]+/g, '') === cleanedPayload;
        const matchesUser = o.userPublicIdentifier && cleanLeadingZeros(o.userPublicIdentifier.replace(/[^0-9]+/g, '')) === cleanLeadingZeros(cleanedPayload);

        if (!didMatchUser && matchesUser) {
          didMatchUser = matchesUser;
        }

        return matchesGuid || matchesUser || matchesParentGuid || matchesPickupCode;
      });

    if (localSettings && localSettings.selectOrdersOnUserMatch && didMatchUser) {
      const visibleOrders = yield select(state => getVisibleOrders(state));

      const matchingOrdersVisible = visibleOrders.filter((o) => matchingOrders.some(mo => mo.id === o.id));

      for (let i = 0; i < matchingOrdersVisible.length; i++) {
        yield put(selectOrder(matchingOrdersVisible[i]));

         // check for not false for migration reasons
          if (!localSettings || localSettings.autoNextOnScan !== false) {                        
            if (localSettings.restrictAutoNextOnScanToToday !== false) {
              if (selectedMode === 'Morgen' || selectedMode == 'Uebermorgen' || (selectedMode === 'more' && ordersEndDate && !moment(ordersEndDate).isSame(moment(), 'day'))) {
                yield put(errorNotification('Automatische Bestätigung nur für aktuelle Tag erlaubt.'));
                return;
              }
            }        
            yield put(proceedOrderAction({ item: matchingOrdersVisible[i] }));

            const resetDelay = (localSettings.autoNextTimeout || 4);
            const delayMs = Number(resetDelay) * 1000;

            yield delay(delayMs);
            const selectedOrder = yield select(state => state.orders.selectedOrder);

            if (selectedOrder && selectedOrder.id === matchingOrdersVisible[i].id) {
              yield put(selectOrder(null));
            }
          }
      }

      return;
    }

      const groupByProductionPlaces = !!localSettings.showProductionPlaceFilter;
      console.log('Found matching orders: ', matchingOrders.length, ' did match by user identifier', didMatchUser);

      if (!didMatchUser && matchingOrders.length === 1) {
        order = matchingOrders[0];
      } else if (!didMatchUser && matchingOrders.length > 1 && groupByProductionPlaces) {
        const mergedOrders = mergeOrders(matchingOrders);

        if (mergeOrders.length == 1) {
          order = mergedOrders[0];
        }
      } else if (matchingOrders.length > 1 || (didMatchUser && matchingOrders.length > 0 && !localSettings.selectOrdersOnUserMatch)) {
        yield put(setOrderSearchValue(cleanedPayload));
        yield put(selectOrder(null));

        yield put(resetOrderSearchValue());
        return;
      }
    }

    if (order == null || order === undefined) {
      if (localSettings && localSettings.playSoundOnNoOrderFound) {
        const audio = new Audio(`${process.env.PUBLIC_URL}/invalid.mp3`);
        audio.play();
      }

      yield put(errorNotification(`Bestellung nicht gefunden! (${cleanedPayload})`));
      return;
    }

    if (!didMatchUser) {
      yield put(selectOrder(order));

      // check for not false for migration reasons
      if (!localSettings || localSettings.autoNextOnScan !== false) {
        yield put(proceedOrderAction({ item: order }));
        const resetDelay = (localSettings.autoNextTimeout || 4);
        const delayMs = Number(resetDelay) * 1000;

        yield delay(delayMs);
        const selectedOrder = yield select(state => state.orders.selectedOrder);

        if (selectedOrder && selectedOrder.id === order.id) {
          yield put(selectOrder(null));
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
}

export function* handleOrderCardAction(orderApi, { payload }) {
  const { order, proceeded, description } = payload;

  let note; let
    blockUser;

  if (!proceeded) {
    const data = { note, blockUser } = yield select(state => state.alert);
    note = data.note;
    blockUser = data.blockUser;
  }


  yield put(addOrderToHistory({ order, description, note, blockUser, proceeded }));

  if (proceeded) {
    if (order.subOrders && order.subOrders.length) {
      for (let index = 0; index < order.subOrders.length; index++) {
        const subOrderId = order.subOrders[index];
        yield put(proceedOrderAction({ item: { id: subOrderId } }));
      }

    } else {
      yield put(proceedOrderAction({ item: order }));
    }
  } else {
    yield put(declineOrderAction({ order, note, blockUser }));
  }
}

export function* setMode() {
  (yield select(state => state.app.scrollTopOrderGridFunction))();
}

export function* setDeliveryTime(orderApi, { payload }) {
  try {
    const response = yield apply(
      orderApi,
      orderApi.setDeliveryTime,
      [payload],
    );

    if (response.errorCode) {
      yield put(errorNotification(response.message || 'Lieferzeit konnte nicht gesetzt werden!'));
    }
  } catch (e) {
    yield put(errorNotification('Lieferzeit konnte nicht gesetzt werden!'));
  }
}
